import { useEffect } from 'react';
import type { Game } from '@/game/Game';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useServices } from '@/ui/services/ServicesContext';
import styles from './BossScreen.module.css';
import { useBack } from '@/ui/back/backStack';

const NOTES_ICON = './icons/notes.png';

/**
 * S19 "Sếp tới!" (ux-screens §3.19): instantly silent, paused and saved, showing a plain neutral notes page.
 * Only the paw, the hotkey or Esc bring the game back; taps elsewhere do nothing.
 */
export function BossScreen({ game, onBack }: { game: Game; onBack: () => void }) {
  const { t } = useI18n();
  const { platform, store, saves } = useServices();

  useEffect(() => {
    const title = document.title;
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const href = icon?.href;
    platform.sfx.setMuted(true);
    platform.music.setMuted(true);
    platform.audio.setSuspended('boss', true);
    game.setPaused('boss', true);
    game.miniGames.setPaused(true);
    void saves.flush();
    document.title = t('ui.boss.doc_title');
    if (icon) icon.href = NOTES_ICON;
    return () => {
      document.title = title;
      if (icon && href) icon.href = href;
      // Back exactly as before: a player who had muted stays muted.
      platform.sfx.setMuted(store.getState().settings.muted);
      platform.music.setMuted(store.getState().settings.muted);
      platform.audio.setSuspended('boss', false);
      game.setPaused('boss', false);
      game.miniGames.setPaused(false);
    };
  }, [game, platform, saves, store, t]);

  useBack(onBack);

  useEffect(() => {
    const key = store.getState().settings.bossHotkey;
    const onKey = (e: KeyboardEvent) => {
      if (e.code === key) {
        e.preventDefault();
        e.stopPropagation();
        onBack();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
    };
  }, [onBack, store]);

  return (
    <div className={styles.screen} data-testid="boss-screen">
      <div className={styles.bar}>
        <span>{t('ui.boss.doc_title')}</span>
        <span aria-hidden="true">⋯</span>
      </div>
      <div className={styles.doc}>
        <h1 className={styles.heading}>{t('ui.boss.heading')}</h1>
        <div className={styles.line} style={{ width: '82%' }} />
        <div className={styles.line} style={{ width: '64%' }} />
        <div className={styles.bullet} style={{ width: '58%' }} />
        <div className={styles.bullet} style={{ width: '40%' }} />
        <div className={styles.bullet} style={{ width: '52%' }} />
        <div className={styles.line} style={{ width: '74%' }} />
      </div>
      <button type="button" className={styles.paw} aria-label={t('ui.boss.back')} onClick={onBack}>
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <circle cx="12" cy="15" r="4" fill="currentColor" />
          <circle cx="6.5" cy="10" r="2" fill="currentColor" />
          <circle cx="10" cy="6.5" r="2" fill="currentColor" />
          <circle cx="14" cy="6.5" r="2" fill="currentColor" />
          <circle cx="17.5" cy="10" r="2" fill="currentColor" />
        </svg>
      </button>
    </div>
  );
}
